import {
  Component,
  OnInit,
  computed,
  signal,
  inject,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { DataViewModule } from 'primeng/dataview';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { CardModule } from 'primeng/card';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { LocationModel, LocationFilterModel } from '@kanzi-apes/kanzi-models';
import { StoresQueriesService } from '../../../queries';
import { Store } from '@ngrx/store';

@Component({
    selector: 'kanzi-apes-store-list',
    imports: [
        CommonModule,
        TranslateModule,
        ButtonModule,
        DataViewModule,
        InputTextModule,
        SkeletonModule,
        TagModule,
        CardModule,
        ConfirmDialogModule,
        TooltipModule,
        KanziUiModule,
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './store-list.component.html',
    styleUrls: ['./store-list.component.scss']
})
export class StoreListComponent implements OnInit {
  // Inyección de servicios
  private readonly router = inject(Router);
  private readonly queries = inject(StoresQueriesService);
  private readonly confirmService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly store = inject(Store);
  customerSelected = this.store.selectSignal(fromCustomer.getCustomerSelected);

  // Signals para filtros
  filters = signal<LocationFilterModel | null>(null);
  
  // Signal para controlar cuándo ejecutar la query
  isQueryEnabled = signal<boolean>(false);

  customerEffect = effect(
    () => {
      const customer = this.customerSelected();
      console.log('[StoreList] Customer selected:', customer);
      
      // Siempre habilitar el query, con o sin customer
      const newFilters: LocationFilterModel = {
        page: 1,
        page_size: 50,
      };
      
      // Si hay customer, agregarlo a los filtros
      if (customer) {
        newFilters.customer_id = customer.id;
      }
      
      console.log('[StoreList] Setting filters:', newFilters);
      this.filters.set(newFilters);
      this.isQueryEnabled.set(true);
      console.log('[StoreList] Query enabled');
    },
    { allowSignalWrites: true }
  );

  searchText = signal<string>('');

  // Query para lista de stores - solo se ejecuta cuando isQueryEnabled es true
  storesQuery = this.queries.useStoresList(this.filters, this.isQueryEnabled);

  // Computed signals para la UI
  get stores(): LocationModel[] {
    return this.storesQuery.data()?.results || [];
  }

  totalRecords = computed(() => this.storesQuery.data()?.count || 0);
  isLoading = computed(
    () => this.storesQuery.isPending() || this.storesQuery.isFetching()
  );

  // Mutations
  deleteMutation = this.queries.useDeleteStore();

  constructor() {
    console.log('[StoreList] Constructor called');
  }

  ngOnInit(): void {
    console.log('[StoreList] Component initialized');
    console.log('[StoreList] Customer selected:', this.customerSelected());
    console.log('[StoreList] Filters:', this.filters());
    console.log('[StoreList] Query enabled:', this.isQueryEnabled());
    // Query se ejecuta automáticamente
  }

  /**
   * Navegar a crear nuevo store
   */
  onCreateStore(): void {
    this.router.navigate(['locations', 'stores', 'create']);
  }

  /**
   * Navegar a detalle de store
   */
  onViewStore(store: LocationModel): void {
    if (store.id) {
      this.router.navigate(['locations', 'stores', 'detail', store.id]);
    }
  }

  /**
   * Navegar a editar store
   */
  onEditStore(store: LocationModel, event?: Event): void {
    event?.stopPropagation();
    if (store.id) {
      this.router.navigate([
        'locations',
        'stores',
        'edit',
        store.id,
      ]);
    }
  }

  /**
   * Eliminar store con confirmación
   */
  onDeleteStore(store: LocationModel, event?: Event): void {
    event?.stopPropagation();

    this.confirmService.confirm({
      message: `¿Está seguro de eliminar el store "${store.name}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (store.id) {
          this.deleteMutation.mutate(store.id, {
            onSuccess: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Store eliminado',
                detail: `El store "${store.name}" fue eliminado exitosamente`,
              });
            },
            onError: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error al eliminar',
                detail:
                  error.message || 'Ocurrió un error al eliminar el store',
              });
            },
          });
        }
      },
    });
  }

  /**
   * Buscar stores por texto
   */
  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchValue = input.value.trim();

    this.searchText.set(searchValue);

    // Actualizar filtros (cleanFilters remueve valores vacíos)
    this.filters.set({
      ...this.filters(),
      page: 1,
      search: searchValue,
    });
  }

  /**
   * Limpiar búsqueda
   */
  onClearSearch(): void {
    this.searchText.set('');
    const { search, ...rest } = this.filters() || {};
    this.filters.set({
      ...rest,
      page: 1,
    });
  }

  /**
   * Cambiar página
   */
  onPageChange(page: number): void {
    this.filters.set({
      ...this.filters(),
      page: page,
    });
  }

  /**
   * Pre-cargar store al hacer hover (mejora UX)
   */
  onStoreHover(store: LocationModel): void {
    if (store.id) {
      this.queries.prefetchStore(store.id);
    }
  }
}
